import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Typography,
} from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { UserPlannerItemData } from "api/resources/assignments/types"
import ViewPreviousSubmissionDialog from "features/AssignmentResult/ViewPreviousSubmissionDialog"
import { NavArrowDown } from "iconoir-react"
import { useEffect, useState } from "react"
import { formatDate } from "utils/helpers"

type Props = {
  currentSubmissionId?: string
  userPlannerItemData: UserPlannerItemData
  onLoadSubmission?: (latestSubmissionUUID?: string | null) => void
  showNeutralResultTags: boolean
}

const PreviousSubmissionsDropdown = ({
  currentSubmissionId,
  userPlannerItemData,
  onLoadSubmission,
  showNeutralResultTags,
}: Props) => {
  const { data, isSuccess } = useQuery({
    queryFn: () =>
      api.assignments.previousSubmissionsList({
        urlParams: {
          assignmentUUID: userPlannerItemData.learningContent.uuid,
          userPlannerItemId: userPlannerItemData.id,
        },
      }),
    queryKey: queries.assignments.submissionsListByAssignmentId(
      userPlannerItemData.learningContent.id.toString()
    ).queryKey,
    select: data =>
      data?.sort((a, b) =>
        new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1
      ),
  })

  const [viewPreviousSubmissionIndex, setViewPreviousSubmissionIndex] =
    useState<string | null>(null)

  useEffect(() => {
    if (!onLoadSubmission) return

    const submissionId = isSuccess && data?.length > 0 ? data[0].uuid : null
    onLoadSubmission(submissionId)
  }, [isSuccess, data, onLoadSubmission])

  return (
    <>
      <ViewPreviousSubmissionDialog
        open={!!viewPreviousSubmissionIndex}
        onClose={() => setViewPreviousSubmissionIndex(null)}
        title={`Previous Submission`}
        submissionId={viewPreviousSubmissionIndex}
        showNeutralResultTags={showNeutralResultTags}
      />
      {isSuccess && (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Typography
              variant="strongSmallBody"
              className="flex items-center gap-0.5"
            >
              Previous Submissions & Reviews <NavArrowDown />
            </Typography>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {isSuccess &&
              (() => {
                const filtered = data.filter(
                  item => item.uuid !== currentSubmissionId
                )

                if (filtered.length === 0) {
                  return (
                    <div className="p-1 text-sm text-onSurface-500">
                      No previous submission & reviews
                    </div>
                  )
                }

                return filtered.map((item, index) => (
                  <DropdownMenuItem
                    key={item.uuid}
                    onClick={() => {
                      setViewPreviousSubmissionIndex(item.uuid)
                    }}
                  >
                    <div className="flex flex-row items-center gap-2">
                      <Typography variant="strongSmallBody">
                        Submission {filtered.length - index}
                      </Typography>
                      {item.dateCreated && (
                        <Typography
                          className="text-xs text-onSurface-500"
                          variant="smallBody"
                        >
                          ({formatDate(item.dateCreated)})
                        </Typography>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))
              })()}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  )
}

export default PreviousSubmissionsDropdown
